import React, { useState, useEffect, useRef } from 'react';
import { Scale, Award, Users } from 'lucide-react';
import { motion, useInView } from 'framer-motion';

const AboutSection = () => {
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 }); 

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const variants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 1.2,
        ease: "easeOut",
      }
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 1.2,
        ease: "easeOut",
      }
    },
  };
  
  const iconVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      }
    },
  };
  
  const lineVariants = {
    hidden: { scaleX: 0 },
    visible: {
      scaleX: 1,
      transition: {
        duration: 1.0,
        ease: "easeOut",
      }
    },
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: "easeOut" } }
  };

  // Mobile Layout
  if (isMobile) {
    return (
      <motion.div 
        ref={sectionRef} 
        className="bg-transparent py-16"
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        <div className="max-w-4xl mx-auto px-4">
          <motion.div variants={imageVariants} className="flex justify-center mb-8">
            <motion.div
              variants={imageVariants}
              className="relative"
            >
              <div className="w-32 h-32 rounded-full overflow-hidden border-1 border-gray-800 shadow-2xl">
                <img 
                  src="/חן3.jpg"
                  alt="עו״ד חן"
                  className="w-full h-full object-cover"
                />
              </div>
              <motion.div
                variants={iconVariants}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full flex items-center justify-center border-2 border-white"
              >
                <Scale className="w-4 h-4 text-white" />
              </motion.div>
            </motion.div>
          </motion.div>

          <div className="text-center">
            <motion.h2
              variants={itemVariants}
              className="text-3xl font-bold mb-6 bg-gradient-to-r from-white to-gray bg-clip-text"
            >
           אודות המשרד
            </motion.h2>
            
            <motion.div
              variants={itemVariants}
              className="space-y-6 text-gray-700 leading-relaxed"
            >
              <p className="text-lg font-semibold">
                עורכת הדין חן מיכלסון עוסקת בייצוג חייבים ובהובלת הליכי חדלות פירעון והוצאה לפועל, ומביאה עמה ניסיון עשיר בליווי משפטי מקיף ללקוחות פרטיים ולעסקים.
              </p>
              <p className="text-lg font-semibold">
                המשרד מתמקד במתן פתרונות משפטיים יצירתיים ויעילים – החל מהגשת הבקשה לפתיחת ההליך, טיפול בהסרת עיקולים והגבלות, ניהול מו״מ מול נושים ועד לקבלת צו הפטר ומחיקת חובות.
              </p>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl border-l-4 border-blue-500 mt-6"
            >
              <p className="text-blue-800 font-bold text-xl leading-relaxed">
                המשרד רואה בליווי ההליך לא רק מענה משפטי אלא גם הזדמנות אמיתית לשיקום כלכלי, יציאה לדרך חדשה והשגת יציבות וביטחון לטווח ארוך.
              </p>
            </motion.div>

       
          </div>
        </div>
      </motion.div>
    );
  }

  // Desktop Layout
  return (
    <motion.div 
      ref={sectionRef} 
      className="bg-gradient-to-br from-slate-50 via-white to-slate-100 py-20"
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="max-w-7xl mx-auto px-8 lg:px-16">
        <div className="grid lg:grid-cols-3 gap-16 items-center">
          <div className="lg:col-span-1 flex justify-center lg:justify-end">
            <motion.div
              variants={imageVariants}
              className="relative"
            >
              <div className="relative w-80 h-80 p-1 rounded-full bg-gradient-to-tr from-black to-yellow-500 transform hover:scale-105 transition-transform duration-500">
                <div className="w-full h-full rounded-full overflow-hidden">
                  <img
                    src="/חן3.jpg"
                    alt="עו״ד חן"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              <div className="absolute inset-0 -z-10 w-80 h-80 bg-gradient-to-br from-gray-200/30 to-gray-300/30 rounded-full transform scale-110 blur-3xl"></div>
            </motion.div>
          </div>

          <div className="lg:col-span-2 space-y-8">
            <motion.div variants={itemVariants}>
              <h2 className="text-4xl xl:text-5xl font-bold mb-6 bg-gradient-to-r from-gray to-white bg-clip-text">
                אודות המשרד
              </h2>
              <motion.div
                variants={lineVariants}
                transition={{ delay: 0.2 }}
                className="w-24 h-1 bg-transparent mb-8 origin-left"
              ></motion.div>
            </motion.div>
            
            <motion.div
              variants={itemVariants}
              className="space-y-6 text-gray-700 leading-relaxed"
            >
              <p className="text-xl font-semibold">
                עורכת הדין חן מיכלסון עוסקת בייצוג חייבים ובהובלת הליכי חדלות פירעון והוצאה לפועל, ומביאה עמה ניסיון עשיר בליווי משפטי מקיף ללקוחות פרטיים ולעסקים.
              </p>
              <p className="text-xl font-semibold">
                המשרד מתמקד במתן פתרונות משפטיים יצירתיים ויעילים – החל מהגשת הבקשה לפתיחת ההליך, טיפול בהסרת עיקולים והגבלות, ניהול מו״מ מול נושים ועד לקבלת צו הפטר ומחיקת חובות.
              </p>
            </motion.div>
            
            <motion.div
              variants={itemVariants}
              className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl border-l-4 border-blue-500"
            >
              <p className="text-blue-800 font-bold text-2xl leading-relaxed">
                המשרד רואה בליווי ההליך לא רק מענה משפטי אלא גם הזדמנות אמיתית לשיקום כלכלי, יציאה לדרך חדשה והשגת יציבות וביטחון לטווח ארוך.
              </p>
            </motion.div>

 
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AboutSection;